import { equals } from '@execonline-inc/collections';
import { when } from '@execonline-inc/maybe-adapter';
import { Maybe } from 'maybeasy';
import {
  ClientExperienceResource,
  EditLearningCollectionResource,
  LearningCollectionResource,
  ProductCollectionResource,
  ProductCollectionResourceArray,
} from '../Types';

const checkIfBelongsTo = (
  collection: ProductCollectionResource,
  index: number,
  stateIndex: number,
) => when(equals(index, stateIndex), collection);

const whenIds = (
  collection: ProductCollectionResource,
  index: number,
  stateIndex: number,
): Maybe<ProductCollectionResource> => checkIfBelongsTo(collection, index, stateIndex);

const experienceNotInCollection =
  (experienceId: number) =>
  (collection: ProductCollectionResource): Maybe<ProductCollectionResource> =>
    when(!collection.payload.experiences.includes(experienceId), collection);

// Modifying programs in a Browse All section converts it to a regular section
// by setting browse_all to false, allowing another Browse All to be added.
const addExperienceToCollection =
  (experienceId: number) =>
  (collection: ProductCollectionResource): ProductCollectionResource => ({
    ...collection,
    payload: {
      ...collection.payload,
      experiences: [...collection.payload.experiences, experienceId],
      browse_all: false,
    },
  });

const updatedResourceState = (
  collections: ProductCollectionResourceArray,
  resourceState: EditLearningCollectionResource,
): EditLearningCollectionResource => ({
  ...resourceState,
  payload: {
    ...resourceState.payload,
    product_collections: collections,
  },
});

const returnCollectionUnchanged =
  (collection: ProductCollectionResource) => (): ProductCollectionResource =>
    collection;

const filterOutExperience = (
  collection: ProductCollectionResource,
  experienceId: number,
): number[] => collection.payload.experiences.filter((experience) => experience !== experienceId);

// Modifying programs in a Browse All section converts it to a regular section
// by setting browse_all to false, allowing another Browse All to be added.
const removeExperienceFromCollection =
  (experienceId: number) =>
  (collection: ProductCollectionResource): ProductCollectionResource => ({
    ...collection,
    payload: {
      ...collection.payload,
      experiences: filterOutExperience(collection, experienceId),
      browse_all: false,
    },
  });

const experienceInCollection =
  (experienceId: number) =>
  (collection: ProductCollectionResource): Maybe<ProductCollectionResource> =>
    when(collection.payload.experiences.includes(experienceId), collection);

const removeCollections = (
  experienceId: number,
  resourceState: EditLearningCollectionResource,
  index: number,
): ProductCollectionResourceArray =>
  resourceState.payload.product_collections.map((collection, stateIndex) =>
    whenIds(collection, index, stateIndex)
      .andThen(experienceInCollection(experienceId))
      .map(removeExperienceFromCollection(experienceId))
      .getOrElse(returnCollectionUnchanged(collection)),
  );

export const handleRemoveExperience = (
  experienceId: number,
  resourceState: EditLearningCollectionResource,
  setResourceState: (state: EditLearningCollectionResource) => void,
  index: number,
): void => {
  setResourceState(
    updatedResourceState(removeCollections(experienceId, resourceState, index), resourceState),
  );
};

const addCollections = (
  experienceId: number,
  resourceState: EditLearningCollectionResource,
  index: number,
): ProductCollectionResourceArray =>
  resourceState.payload.product_collections.map((collection, stateIndex) =>
    whenIds(collection, index, stateIndex)
      .andThen(experienceNotInCollection(experienceId))
      .map(addExperienceToCollection(experienceId))
      .getOrElse(returnCollectionUnchanged(collection)),
  );

// Bulk add/remove (Quick Add) builds every section change from one snapshot and sets state
// once; calling the single-course handlers in a loop would keep only the last change.
const addExperiencesToCollection =
  (experienceIds: number[]) =>
  (collection: ProductCollectionResource): ProductCollectionResource => ({
    ...collection,
    payload: {
      ...collection.payload,
      experiences: [
        ...collection.payload.experiences,
        ...experienceIds.filter((id) => !collection.payload.experiences.includes(id)),
      ],
      browse_all: false,
    },
  });

const removeExperiencesFromCollection =
  (experienceIds: number[]) =>
  (collection: ProductCollectionResource): ProductCollectionResource => ({
    ...collection,
    payload: {
      ...collection.payload,
      experiences: collection.payload.experiences.filter((id) => !experienceIds.includes(id)),
      browse_all: false,
    },
  });

const updateSection = (
  update: (collection: ProductCollectionResource) => ProductCollectionResource,
  resourceState: LearningCollectionResource,
  index: number,
): LearningCollectionResource => ({
  ...resourceState,
  payload: {
    ...resourceState.payload,
    product_collections: resourceState.payload.product_collections.map((collection, stateIndex) =>
      whenIds(collection, index, stateIndex)
        .map(update)
        .getOrElse(returnCollectionUnchanged(collection)),
    ),
  },
});

// Note: scheduled_programs are automatically synced by LearningCollectionContext
// when product_collections change, so we only need to update product_collections here
export const handleAddExperiences = (
  experienceIds: number[],
  resourceState: LearningCollectionResource,
  setResourceState: (state: LearningCollectionResource) => void,
  index: number,
): void => {
  if (experienceIds.length === 0) return;
  setResourceState(updateSection(addExperiencesToCollection(experienceIds), resourceState, index));
};

export const handleRemoveExperiences = (
  experienceIds: number[],
  resourceState: LearningCollectionResource,
  setResourceState: (state: LearningCollectionResource) => void,
  index: number,
): void => {
  if (experienceIds.length === 0) return;
  setResourceState(
    updateSection(removeExperiencesFromCollection(experienceIds), resourceState, index),
  );
};

// Note: scheduled_programs are automatically synced by LearningCollectionContext
// when product_collections change, so we only need to update product_collections here
export const handleAddExperience = (
  experienceResource: ClientExperienceResource,
  resourceState: LearningCollectionResource,
  setResourceState: (state: LearningCollectionResource) => void,
  index: number,
): void => {
  const experienceId = experienceResource.payload.id;
  const updatedCollections = addCollections(experienceId, resourceState, index);

  setResourceState({
    ...resourceState,
    payload: {
      ...resourceState.payload,
      product_collections: updatedCollections,
    },
  });
};
